import React, { useState, useEffect } from 'react';
import { clientsAPI, certificateAPI } from '../../services/api';
import {
  X,
  Eye,
  EyeOff,
  Shield,
  ShieldCheck,
  ShieldAlert,
  FileText,
  Key,
  Lock,
  CheckCircle,
  AlertCircle,
  Info,
  Upload,
  Loader2,
  User,
  Building,
  Calendar
} from 'lucide-react';

const ClientModal = ({ client, onClose }) => {
  const [formData, setFormData] = useState({
    rfc: '',
    razonSocial: '',
    certificateData: '',
    privateKeyData: '',
    certificatePassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [certificateValid, setCertificateValid] = useState(null);
  const [certificateInfo, setCertificateInfo] = useState(null);
  const [validatingCertificate, setValidatingCertificate] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (client) {
      setFormData({
        rfc: client.rfc || '',
        razonSocial: client.razonSocial || '',
        certificateData: '',
        privateKeyData: '',
        certificatePassword: ''
      });
    }
  }, [client]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleFileChange = async (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result.split(',')[1]; // Remover el prefijo data:

        setFormData({
          ...formData,
          [fieldName]: base64
        });

        // Si es el certificado (.cer), validarlo automáticamente
        if (fieldName === 'certificateData') {
          await validateCertificate(base64);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const validateCertificate = async (certificateBase64) => {
    try {
      setValidatingCertificate(true);
      setError('');

      console.log('🔍 Validando certificado...');
      const response = await certificateAPI.testCertificate(certificateBase64);

      console.log('✅ Certificado validado:', response.data);
      setCertificateInfo(response.data);

      // Extraer RFC y Razón Social del certificado según la estructura de tu API
      if (response.data.certificateInfo && response.data.certificateInfo.rfc) {
        setFormData(prev => ({
          ...prev,
          rfc: response.data.certificateInfo.rfc,
          razonSocial: response.data.certificateInfo.subject || prev.razonSocial
        }));

        console.log('📋 RFC extraído:', response.data.certificateInfo.rfc);
        console.log('📋 Subject extraído:', response.data.certificateInfo.subject);
      }

    } catch (error) {
      console.error('❌ Error validando certificado:', error);
      setError('Error al validar certificado: ' + (error.response?.data?.message || error.message));
      setCertificateInfo(null);
    } finally {
      setValidatingCertificate(false);
    }
  };

  const validateCredentials = async () => {
    if (!formData.certificateData || !formData.privateKeyData || !formData.certificatePassword) {
      setError('Debe cargar el certificado, la llave privada y proporcionar la contraseña');
      return;
    }

    setValidatingCertificate(true);
    setError('');
    setCertificateValid(null);
    setCertificateInfo(null);

    try {
      console.log('🔍 Validando credenciales completas...');
      const response = await certificateAPI.testCredential({
        certificateBase64: formData.certificateData,
        privateKeyBase64: formData.privateKeyData,
        password: formData.certificatePassword
      });

      console.log('✅ Respuesta de validación:', response.data);

      // Verificar si la validación fue exitosa según tu nueva estructura
      if (response.data.success && response.data.credentialTest?.isCredentialValid) {
        setCertificateValid(true);
        setCertificateInfo(response.data.certificateInfo);

        // Auto-completar RFC y Razón Social si están disponibles
        if (response.data.certificateInfo?.rfc) {
          setFormData(prev => ({
            ...prev,
            rfc: response.data.certificateInfo.rfc,
            razonSocial: response.data.certificateInfo.subject || prev.razonSocial
          }));
        }

        console.log('✅ Credenciales válidas - RFC:', response.data.certificateInfo?.rfc);
        setError('');
      } else {
        setCertificateValid(false);
        setError('Las credenciales no son válidas. Verifique el certificado, llave privada y contraseña.');
      }

    } catch (error) {
      setCertificateValid(false);
      setError('Error validando credenciales: ' + (error.response?.data?.message || error.message));
      console.error('❌ Error validando credenciales:', error);
    } finally {
      setValidatingCertificate(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (client) {
        // Actualizar cliente existente
        await clientsAPI.update(client.id, formData);
      } else {
        // Crear nuevo cliente
        await clientsAPI.create(formData);
      }
      onClose(true); // true indica que debe recargar la lista
    } catch (error) {
      setError(error.response?.data?.message || 'Error al guardar cliente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-3xl w-full max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="modal-header">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
              <User className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {client ? 'Editar Cliente' : 'Nuevo Cliente'}
            </h2>
          </div>
          <button
            onClick={() => onClose(false)}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Error Alert */}
          {error && (
            <div className="alert alert-error mb-6">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1">{error}</span>
              <button
                onClick={() => setError('')}
                className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* RFC Field */}
            <div>
              <label htmlFor="rfc" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center space-x-2">
                  <span>RFC *</span>
                  {certificateInfo && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Extraído del certificado
                    </span>
                  )}
                </div>
              </label>
              <input
                type="text"
                id="rfc"
                name="rfc"
                className={`input-field uppercase ${certificateInfo ? 'bg-gray-50 dark:bg-gray-600 cursor-not-allowed' : ''}`}
                value={formData.rfc}
                onChange={handleChange}
                placeholder="XAXX010101000"
                maxLength="13"
                required
                readOnly={certificateInfo !== null}
              />
              {certificateInfo && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Este campo se llenó automáticamente desde el certificado
                </p>
              )}
            </div>

            {/* Razón Social Field */}
            <div>
              <label htmlFor="razonSocial" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center space-x-2">
                  <span>Razón Social *</span>
                  {certificateInfo && certificateInfo.razonSocial && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800/20 dark:text-blue-400">
                      <FileText className="w-3 h-3 mr-1" />
                      Sugerida del certificado
                    </span>
                  )}
                </div>
              </label>
              <input
                type="text"
                id="razonSocial"
                name="razonSocial"
                className="input-field"
                value={formData.razonSocial}
                onChange={handleChange}
                placeholder="Mi Empresa SA de CV"
                required
              />
              {certificateInfo && certificateInfo.razonSocial && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Razón social extraída del certificado (puedes editarla si es necesario)
                </p>
              )}
            </div>

            {/* Certificate File */}
            <div>
              <label htmlFor="certificate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center space-x-2">
                  <Shield className="w-4 h-4" />
                  <span>Certificado (.cer) *</span>
                  {validatingCertificate && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800/20 dark:text-blue-400">
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      Validando...
                    </span>
                  )}
                  {certificateInfo && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400">
                      <ShieldCheck className="w-3 h-3 mr-1" />
                      Válido
                    </span>
                  )}
                </div>
              </label>
              <div className="flex items-center space-x-3">
                <div className="flex-1">
                  <input
                    type="file"
                    id="certificate"
                    className="input-field"
                    accept=".cer"
                    onChange={(e) => handleFileChange(e, 'certificateData')}
                    required={!client}
                    disabled={validatingCertificate}
                  />
                </div>
                <Upload className="w-5 h-5 text-gray-400" />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Archivo de certificado del SAT (.cer) - Se validará automáticamente
              </p>

              {/* Certificate Info */}
              {certificateInfo && certificateInfo.certificateInfo && (
                <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <h4 className="flex items-center text-sm font-medium text-green-800 dark:text-green-300 mb-3">
                    <FileText className="w-4 h-4 mr-2" />
                    Información del Certificado
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                    <div>
                      <span className="font-medium text-green-700 dark:text-green-400">RFC:</span>
                      <span className="ml-2 text-green-800 dark:text-green-300">{certificateInfo.certificateInfo.rfc}</span>
                    </div>
                    <div>
                      <span className="font-medium text-green-700 dark:text-green-400">Titular:</span>
                      <span className="ml-2 text-green-800 dark:text-green-300">{certificateInfo.certificateInfo.subject}</span>
                    </div>
                    <div>
                      <span className="font-medium text-green-700 dark:text-green-400">Válido desde:</span>
                      <span className="ml-2 text-green-800 dark:text-green-300">
                        {new Date(certificateInfo.certificateInfo.validFrom).toLocaleDateString('es-ES')}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-green-700 dark:text-green-400">Válido hasta:</span>
                      <span className="ml-2 text-green-800 dark:text-green-300">
                        {new Date(certificateInfo.certificateInfo.validTo).toLocaleDateString('es-ES')}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-green-700 dark:text-green-400">Días hasta vencimiento:</span>
                      <span className="ml-2 text-green-800 dark:text-green-300">
                        {Math.floor(certificateInfo.technicalDetails.daysUntilExpiration)} días
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-green-700 dark:text-green-400">Estado:</span>
                      <span className={`ml-2 font-medium ${certificateInfo.certificateInfo.isValidNow
                          ? 'text-green-800 dark:text-green-300'
                          : 'text-red-800 dark:text-red-300'
                        }`}>
                        {certificateInfo.certificateInfo.isValidNow ? '✅ Certificado válido' : '❌ Certificado inválido'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Private Key File */}
            <div>
              <label htmlFor="privateKey" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center space-x-2">
                  <Key className="w-4 h-4" />
                  <span>Llave Privada (.key) *</span>
                </div>
              </label>
              <div className="flex items-center space-x-3">
                <div className="flex-1">
                  <input
                    type="file"
                    id="privateKey"
                    className="input-field"
                    accept=".key"
                    onChange={(e) => handleFileChange(e, 'privateKeyData')}
                    required={!client}
                  />
                </div>
                <Upload className="w-5 h-5 text-gray-400" />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Archivo de llave privada del SAT (.key)
              </p>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="certificatePassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center space-x-2">
                  <Lock className="w-4 h-4" />
                  <span>Contraseña del Certificado *</span>
                </div>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="certificatePassword"
                  name="certificatePassword"
                  className="input-field pr-10"
                  value={formData.certificatePassword}
                  onChange={handleChange}
                  placeholder="Contraseña del certificado"
                  required={!client}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Validation Status */}
            {certificateValid === true && certificateInfo && (
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-center space-x-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  <h4 className="font-medium text-green-800 dark:text-green-300">
                    Credenciales válidas
                  </h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-green-700 dark:text-green-300">
                  <div><strong>RFC:</strong> {certificateInfo.rfc}</div>
                  <div><strong>Titular:</strong> {certificateInfo.subject}</div>
                  <div><strong>Tipo:</strong> FIEL</div>
                  <div><strong>Válido desde:</strong> {new Date(certificateInfo.validFrom).toLocaleDateString('es-ES')}</div>
                  <div><strong>Válido hasta:</strong> {new Date(certificateInfo.validTo).toLocaleDateString('es-ES')}</div>
                  <div>
                    <strong>Estado:</strong>
                    <span className={certificateInfo.isValidNow ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                      {certificateInfo.isValidNow ? ' 🟢 Vigente' : ' 🔴 Expirado'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {certificateValid === false && (
              <div className="alert alert-error">
                <ShieldAlert className="w-5 h-5 flex-shrink-0" />
                <span>Credenciales inválidas - Verifique el certificado, llave privada y contraseña</span>
              </div>
            )}

            {/* Info for new clients */}
            {!client && certificateValid !== true && (
              <div className="alert alert-warning">
                <Info className="w-5 h-5 flex-shrink-0" />
                <span>Debe validar las credenciales del certificado antes de crear el cliente</span>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            type="button"
            onClick={() => onClose(false)}
            disabled={loading}
            className="btn-ghost"
          >
            Cancelar
          </button>

          {/* Validate button for new clients */}
          {!client && certificateValid !== true && (
            <button
              type="button"
              onClick={validateCredentials}
              disabled={validatingCertificate || !formData.certificateData || !formData.privateKeyData || !formData.certificatePassword}
              className="btn-secondary"
            >
              {validatingCertificate ? (
                <div className="flex items-center space-x-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Validando...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <ShieldCheck className="w-4 h-4" />
                  <span>Validar Credenciales</span>
                </div>
              )}
            </button>
          )}

          {/* Create button for new clients (only when validated) */}
          {!client && certificateValid === true && (
            <button
              type="submit"
              form="client-form"
              disabled={loading}
              className="btn-primary"
              onClick={handleSubmit}
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Creando...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>Crear Cliente</span>
                </div>
              )}
            </button>
          )}

          {/* Update button for existing clients */}
          {client && (
            <button
              type="submit"
              form="client-form"
              disabled={loading}
              className="btn-primary"
              onClick={handleSubmit}
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Actualizando...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>Actualizar</span>
                </div>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientModal;